import { PortfolioEngineAccount } from '@ndvr/portfolio-api';

import { ConfirmModal } from '@/components/ConfirmModal';

const MAX_LISTED_ACCOUNTS = 10;

export type TradeStatusAction = 'suspend' | 'enable';

interface Props {
    accounts: PortfolioEngineAccount[];
    action: TradeStatusAction;
    totalSelectedCount: number;
    isPending: boolean;
    onConfirm: () => void;
    onClose: () => void;
}

export const TradeStatusConfirmDialog = ({
    accounts,
    action,
    totalSelectedCount,
    isPending,
    onConfirm,
    onClose,
}: Props) => {
    const verbTitleCase = action === 'suspend' ? 'Suspend' : 'Enable';

    return (
        <ConfirmModal
            open
            onOpenChange={(open) => {
                if (!open) onClose();
            }}
            title={`${verbTitleCase} trading`}
            description={
                <TradeStatusConfirmBody accounts={accounts} action={action} totalSelectedCount={totalSelectedCount} />
            }
            confirmText={`${verbTitleCase} trading`}
            confirmVariant={action === 'suspend' ? 'destructive' : 'default'}
            loading={isPending}
            closeOnConfirm={false}
            onConfirm={onConfirm}
        />
    );
};

const TradeStatusConfirmBody = ({
    accounts,
    action,
    totalSelectedCount,
}: {
    accounts: PortfolioEngineAccount[];
    action: TradeStatusAction;
    totalSelectedCount: number;
}) => {
    const verb = action === 'suspend' ? 'suspend' : 'enable';
    const shown = accounts.slice(0, MAX_LISTED_ACCOUNTS);
    const remaining = accounts.length - shown.length;
    const subject = accounts.length === 1 ? 'this account' : `these ${accounts.length} accounts`;
    const skipped = totalSelectedCount - accounts.length;

    return (
        <div className="flex flex-col gap-2">
            <p>
                Are you sure you want to {verb} trading on {subject}?
            </p>
            <ul className="text-cocoa-700 list-disc pl-5 text-sm">
                {shown.map((account) => (
                    <li key={account.accountGUID}>{account.accountNamePlain ?? account.accountGUID}</li>
                ))}
                {remaining > 0 && (
                    <li className="list-none italic">
                        … and {remaining} more {remaining === 1 ? 'account' : 'accounts'}
                    </li>
                )}
            </ul>
            {skipped > 0 && (
                <p className="text-cocoa-600 text-sm italic">
                    {skipped === 1
                        ? '1 other selected account cannot be changed for this action and will be skipped.'
                        : `${skipped} other selected accounts cannot be changed for this action and will be skipped.`}
                </p>
            )}
        </div>
    );
};
